

import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, MessageCircle, Globe, MapPin, Mail, ChevronLeft, ChevronRight, 
  Facebook, Linkedin, Instagram, Twitter, Send, Save, Download, Share2, 
  UserPlus, Edit, LogOut, X, Grid, ArrowLeft 
} from 'lucide-react';
import QRCode from "react-qr-code";
import OTPVerificationModal from '../cardbuilder/OTPVerificationModal';
import html2canvas from 'html2canvas';
import { FiImage, FiVideo, FiFileText, FiFile } from 'react-icons/fi';
import { FaShare } from "react-icons/fa";

// --- MOCK DATA FOR EASY UNDERSTANDING ---
const defaultData = {
  companyName: '',
  image: '',
  name: '',
  designation: '',
  phoneNumber: '',
  email: '',
  address: '',
  location: '',
  whatsappChat: '',
  call: '',
  facebookUrl: '',
  linkedinUrl: '',
  instagramUrl: '',
  twitterUrl: '',
  telegramUrl: '',
  pdf: '',
  ppt: '',
  videoUrl1: '',
  videoUrl2: '',
  website: '',
  websiteUrl: '',
  showExtraInputs: false,
  extraField1: '',
  extraField2: '',
  extraField3: '',
};

const defaultTheme = {
  mode: 'light',
  colors: { background: '#f3f4f6', cardBg: '#ffffff', text: '#1f2937', textSecondary: '#6b7280', primary: '#3b82f6', secondary: '#10b981', border: '#e5e7eb', buttonBg: '#3b82f6', buttonText: '#ffffff', accent: '#8b5cf6' },
  fonts: { heading: 'font-sans', body: 'font-sans' },
  styles: { buttonRadius: '12px', backgroundPattern: '' }
};

const FinalCardView = ({ data = defaultData, theme = defaultTheme, onBack, onSave, userEmail, userMobile, isPublicView = false }) => {

  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const cardRef = useRef(null);

  // Modal & Gallery States
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGridView, setIsGridView] = useState(false); 

  // Media Logic
  const mediaItems = [
    ...(data.image1 ? [{ type: 'image1', url: data.image1 }] : []),
    ...(data.image2 ? [{ type: 'image2', url: data.image2 }] : []),
    ...(data.image3 ? [{ type: 'image3', url: data.image3 }] : []),
    ...(data.image4 ? [{ type: 'image4', url: data.image4 }] : []),
    ...(data.image5 ? [{ type: 'image5', url: data.image5 }] : []),
    ...(data.image6 ? [{ type: 'image6', url: data.image6 }] : []),
    ...(data.image7 ? [{ type: 'image7', url: data.image7 }] : []),
    ...(data.image8 ? [{ type: 'image8', url: data.image8 }] : []),
    ...(data.image9 ? [{ type: 'image9', url: data.image9 }] : []),
    ...(data.video1 ? [{ type: 'video', url: data.video1 }] : []),
  ];

  const nextMedia = () => setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
  const prevMedia = () => setCurrentMediaIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);

  // Social Links Filter
  const socialLinks = [
    { icon: Facebook, url: data.facebookUrl, color: '#1877F2', label: 'Facebook' },
    { icon: Linkedin, url: data.linkedinUrl, color: '#0A66C2', label: 'LinkedIn' },
    { icon: Instagram, url: data.instagramUrl, color: '#E4405F', label: 'Instagram' },
    { icon: Twitter, url: data.twitterUrl, color: '#1DA1F2', label: 'Twitter' },
    { icon: Send, url: data.telegramUrl, color: '#0088CC', label: 'Telegram' },
  ].filter(link => link.url);

  // Handlers
  const handleDownloadVCard = () => {
    const vcard = `BEGIN:VCARD\nVERSION:3.0\nFN:${data.name}\nTEL:${data.phoneNumber}\nEMAIL:${data.email}\nEND:VCARD`;
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data.name}.vcf`;
    link.click();
  };

  const handleDownload = async () => {
    if (!cardRef.current) {
      alert("Card element not found! Make sure the card is visible on screen.");
      return;
    }

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: theme.colors.cardBg,
      });

      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `${data.name || 'business'}_card.png`;
      link.click();
    } catch (error) {
      console.error("Capture failed:", error);
    }
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");

    if (confirmLogout) {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/";
    }
  };

  const openImageModal = () => {
    const imageFields = [
      data.image1, data.image2, data.image3, data.image4, data.image5,
      data.image6, data.image7, data.image8, data.image9, data.image10
    ];

    const validImages = imageFields.filter(img => img && img.trim() !== "");

    if (validImages.length > 0) {
      setModalImages(validImages);
      setCurrentImageIndex(0);
      setIsGridView(false); // Open straight into single image gallery view
      setIsImageModalOpen(true);
    } else {
      alert('No valid images available');
    }
  };

  const shareImage = (imageUrl) => {
    if (navigator.canShare && navigator.canShare({ url: imageUrl })) {
      navigator.share({
        title: 'Check out this image',
        text: 'I thought you might like this!',
        url: imageUrl,
      }).catch((error) => console.error('Error sharing:', error));
    } else {
      navigator.clipboard.writeText(imageUrl).then(() => {
        alert('Image URL copied to clipboard. You can now paste it anywhere!');
      }).catch((error) => console.error('Error copying to clipboard:', error));
    }
  };

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const handleOpenVideoModal = () => setIsVideoModalOpen(true);
  const handleCloseVideoModal = () => setIsVideoModalOpen(false);

  const handleOpenVideoInNewTab = (url) => {
    window.open(url, '_blank');
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: theme.colors.cardBg,
      });

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], "business-card.png", { type: "image/png" });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: data.name,
            text: "My Digital Business Card",
            files: [file],
          });
        } else {
          handleDownload();
        }
      }, "image/png");
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  const cardUrl = data?.websiteUrl
    ? `https://nfcdesignstudio.com/cardurl/${data.websiteUrl}`
    : "";

  const handleEditClick = () => setIsVerifyModalOpen(true);
  
  const handleOTPSuccess = () => {
    setIsVerifyModalOpen(false);
    if (onBack) onBack();
  };

  return (
    <>
      {!isPublicView && (
        <div style={{ flexShrink: 0, borderBottom: '1px solid #ccc', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(10px)', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 'bold', margin: 0 }}>Digital Card Builder</h2>
            <p style={{ fontSize: 14, color: '#555', margin: 0 }}>Create your professional NFC business card</p>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <a style={{ textDecoration: 'none', color: '#007bff', fontWeight: '500' }}> MY Card</a>
            {/* <a href="/vfc-card" style={{ textDecoration: 'none', color: '#007bff', fontWeight: '500' }}> VFC Card</a> */}
            <a href="/profile" style={{ textDecoration: 'none', color: '#007bff', fontWeight: '500' }}> Profile</a>
            <a onClick={handleLogout} style={{ display: "flex", alignItems: "center", gap: "6px", textDecoration: "none", color: "#007bff", fontWeight: "500", cursor: "pointer" }}><LogOut size={16} /> Logout</a>
          </div>
        </div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: theme.colors.background, fontFamily: 'sans-serif' }}>
        <div style={{ width: '100%', maxWidth: '450px' }}>

          <div ref={cardRef} style={{ background: theme.colors.cardBg, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)', border: `1px solid ${theme.colors.border}`, padding: '32px' }}>
            {/* Profile Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <img src={data.image} alt="Logo" style={{ width: '96px', height: '96px', borderRadius: '50%', objectFit: 'contain', border: `3px solid ${theme.colors.primary}`, marginBottom: '16px' }} />
              <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: theme.colors.text, margin: '0' }}>{data.name}</h2>
              <p style={{ color: theme.colors.textSecondary, margin: '4px 0' }}>{data.designation}</p>
              <p style={{ color: theme.colors.primary, fontWeight: '600', marginTop: '4px' }}>{data.companyName}</p>
            </div>

            {/* Media Carousel */}
            {mediaItems.length > 0 && (
              <div style={{ position: 'relative', marginBottom: '24px', borderRadius: '16px', overflow: 'hidden' }}>
                <div style={{ aspectRatio: '16/9', background: '#000' }}>
                  <img src={mediaItems[currentMediaIndex].url} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="work" />
                </div>
                {mediaItems.length > 1 && (
                  <>
                    <button onClick={prevMedia} style={carouselButtonStyle('left')}><ChevronLeft size={20} /></button>
                    <button onClick={nextMedia} style={carouselButtonStyle('right')}><ChevronRight size={20} /></button>
                  </>
                )}
              </div>
            )}

            {/* Action Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <a href={`tel:${data.phoneNumber}`} style={actionButtonStyle(theme)}><Phone size={18} /> Call</a>
              <a href={data.whatsappChat} style={actionButtonStyle(theme)}><MessageCircle size={18} /> WhatsApp</a>
              <a href={data.website} target="_blank" rel="noopener noreferrer" style={actionButtonStyle(theme)}><Globe size={18} /> Website</a>
              <a href={data.location} style={actionButtonStyle(theme)}><MapPin size={18} /> Location</a>
              <a href={`mailto:${data.email}`} style={{ ...actionButtonStyle(theme), gridColumn: 'span 2' }}><Mail size={18} /> {data.al_email}</a>

              <button onClick={openImageModal} style={actionButtonStyle(theme)}><FiImage size={18} /> Images</button>
              <button onClick={() => { if (data.videoUrl1 || data.videoUrl2) { handleOpenVideoModal(); } else { alert('No videos available'); } }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderRadius: theme.styles.buttonRadius, background: theme.colors.buttonBg, color: theme.colors.buttonText }}><FiVideo size={18} /> Videos</button>
              <button onClick={() => { if (data.pdf) { window.open(data.pdf, '_blank'); } else { alert('No PDF available'); } }} style={actionButtonStyle(theme)}><FiFileText size={18} /> PDF</button>
              <button onClick={() => { if (data.ppt) { window.open(data.ppt, '_blank'); } else { alert('No PPT available'); } }} style={actionButtonStyle(theme)}><FiFile size={18} /> PPT</button>
            </div>

            {/* Address Box */}
            {data.address && (
              <div style={{ padding: '16px', borderRadius: '12px', background: `${theme.colors.primary}10`, fontSize: '14px', color: theme.colors.textSecondary, textAlign: 'center', lineHeight: '1.5', marginBottom: '24px' }}>{data.address}</div>
            )}

            {/* Social Icons */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', paddingBottom: '20px', borderBottom: `1px solid ${theme.colors.border}` }}>
              {socialLinks.map((social, i) => (
                <a key={i} href={social.url} style={{ width: '44px', height: '44px', borderRadius: '50%', background: social.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><social.icon size={20} /></a>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginBottom: '30px', borderTop: `1px solid ${theme.colors.border}`, paddingTop: '25px' }}>
              <p style={{ fontSize: '14px', color: theme.colors.textSecondary, marginBottom: '12px' }}>Scan to Save Card</p>
              <div style={{ background: 'white', padding: '12px', display: 'inline-block', borderRadius: '16px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                <QRCode value={cardUrl} size={130} style={{ height: "auto", maxWidth: "100%", width: "100%" }} viewBox={`0 0 256 256`} />
              </div>
            </div>

            {/* Bottom Toolbar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', paddingTop: '20px' }}>
              <UtilityBtn icon={Save} label="Save" color={theme.colors.primary} onClick={() => alert('Saved!')} />
              <UtilityBtn icon={Download} label="Download" color={theme.colors.secondary} onClick={handleDownload} />
              <UtilityBtn icon={Share2} label="Share" color={theme.colors.accent} onClick={handleShare} />
              <UtilityBtn icon={UserPlus} label="Contact" color={theme.colors.primary} onClick={handleDownloadVCard} />
              {!isPublicView && (
                <UtilityBtn icon={Edit} label="Edit" color={theme.colors.accent} onClick={handleEditClick} />
              )}
            </div>
          </div>
        </div>

        <OTPVerificationModal isOpen={isVerifyModalOpen} onClose={() => setIsVerifyModalOpen(false)} userEmail={userEmail} userMobile={userMobile} onSuccess={handleOTPSuccess} />
      </motion.div>

      {/* --- ENHANCED RESPONSIVE LIGHTBOX IMAGE MODAL --- */}
      {isImageModalOpen && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(0, 0, 0, 0.92)', 
            display: 'flex', 
            flexDirection: 'column', 
            zIndex: 10000, 
            backdropFilter: 'blur(10px)',
            color: '#ffffff'
          }}
        >
          {/* Top Header Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: 'rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isGridView ? (
                <button 
                  onClick={() => setIsGridView(false)}
                  style={{ background: 'none', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}
                >
                  <ArrowLeft size={18} /> Back to View
                </button>
              ) : (
                <span style={{ fontSize: '14px', fontWeight: '500', opacity: 0.8 }}>
                  {currentImageIndex + 1} / {modalImages.length}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {!isGridView && (
                <button 
                  onClick={() => setIsGridView(true)} 
                  title="Grid View"
                  style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '8px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Grid size={18} />
                </button>
              )}
              <button 
                onClick={() => setIsImageModalOpen(false)} 
                title="Close Modal"
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '8px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Main Modal Content */}
          {isGridView ? (
            /* Grid View Mode */
            <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '16px', maxWidth: '1000px', margin: '0 auto' }}>
                {modalImages.map((image, index) => (
                  <div 
                    key={index} 
                    onClick={() => { setCurrentImageIndex(index); setIsGridView(false); }}
                    style={{ 
                      aspectRatio: '1', 
                      borderRadius: '12px', 
                      overflow: 'hidden', 
                      cursor: 'pointer', 
                      border: currentImageIndex === index ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
                      position: 'relative'
                    }}
                  >
                    <img src={image} alt={`thumbnail-${index}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Single Image Showcase Mode */
            <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
              
              {/* Previous Button */}
              {modalImages.length > 1 && (
                <button 
                  onClick={() => setCurrentImageIndex((prev) => (prev - 1 + modalImages.length) % modalImages.length)}
                  style={{ position: 'absolute', left: '16px', zIndex: 10, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '12px', borderRadius: '50%', cursor: 'pointer', backdropFilter: 'blur(4px)' }}
                >
                  <ChevronLeft size={24} />
                </button>
              )}

              {/* Displayed Image */}
              <div style={{ maxWidth: '100%', maxHeight: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img 
                  src={modalImages[currentImageIndex]} 
                  alt={`gallery-${currentImageIndex}`} 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '70vh', 
                    objectFit: 'contain', 
                    borderRadius: '8px', 
                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)'
                  }} 
                />
              </div>

              {/* Next Button */}
              {modalImages.length > 1 && (
                <button 
                  onClick={() => setCurrentImageIndex((prev) => (prev + 1) % modalImages.length)}
                  style={{ position: 'absolute', right: '16px', zIndex: 10, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '12px', borderRadius: '50%', cursor: 'pointer', backdropFilter: 'blur(4px)' }}
                >
                  <ChevronRight size={24} />
                </button>
              )}
            </div>
          )}

          {/* Bottom Bar Options (Single View Only) */}
          {!isGridView && (
            <div style={{ padding: '16px', background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  onClick={() => shareImage(modalImages[currentImageIndex])} 
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '20px', background: theme.colors.primary, color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer' }}
                >
                  <FaShare size={14} /> Share Image
                </button>
              </div>

              {/* Bottom Thumbnail Strip */}
              {modalImages.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', maxWidth: '100%', padding: '4px' }}>
                  {modalImages.map((img, idx) => (
                    <img 
                      key={idx}
                      src={img} 
                      alt={`strip-${idx}`} 
                      onClick={() => setCurrentImageIndex(idx)}
                      style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '6px', 
                        objectFit: 'cover', 
                        cursor: 'pointer',
                        opacity: idx === currentImageIndex ? 1 : 0.4,
                        border: idx === currentImageIndex ? `2px solid ${theme.colors.primary}` : 'none',
                        transition: '0.2s'
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', position: 'relative', maxWidth: '90%', maxHeight: '80vh', overflow: 'hidden' }}>
            <button onClick={handleCloseVideoModal} style={{ position: 'absolute', top: '10px', right: '10px', background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' }}>X</button>
            <h5 style={{ textAlign: 'center' }}>Available Videos</h5>
            <ul style={{ listStyleType: 'none', padding: 0, textAlign: 'center' }}>
              {data.videoUrl1 && (<li style={{ marginBottom: '10px' }}><button onClick={() => handleOpenVideoInNewTab(data.videoUrl1)} style={{ padding: '10px 20px', background: theme.colors.buttonBg, color: theme.colors.buttonText, borderRadius: '8px', cursor: 'pointer', textDecoration: 'none' }}>Open Video 1</button></li>)}
              {data.videoUrl2 && (<li style={{ marginBottom: '10px' }}><button onClick={() => handleOpenVideoInNewTab(data.videoUrl2)} style={{ padding: '10px 20px', background: theme.colors.buttonBg, color: theme.colors.buttonText, borderRadius: '8px', cursor: 'pointer', textDecoration: 'none' }}>Open Video 2</button></li>)}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

// --- Helper Components & Styles ---
const actionButtonStyle = (theme) => ({ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderRadius: theme.styles.buttonRadius, background: theme.colors.buttonBg, color: theme.colors.buttonText, textDecoration: 'none', fontSize: '14px', fontWeight: '600', transition: '0.2s' });

const carouselButtonStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: '10px',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(255,255,255,0.8)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const UtilityBtn = ({ icon: Icon, label, color, onClick }) => (
  <button onClick={onClick} style={{ border: 'none', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
    <div style={{ padding: '10px', borderRadius: '12px', background: `${color}15`, color: color }}>
      <Icon size={20} />
    </div>
    <span style={{ fontSize: '10px', fontWeight: '600', color: '#666' }}>{label}</span>
  </button>
);

export default FinalCardView;